import React, { useState } from 'react';
import * as Page from '../../components/content/page-header';
import { Content } from '../../components/layout/content';
import { Label, TextInputLarge } from '../../components/forms/input';
import { DatePicker } from '../../components/forms/date';
import * as Buttons from '../../components/button/button';
import { PaginatedTable } from '../../components/table/paginated-table';
import {
  CellDateRenderer,
  CellJsonRenderer,
  CellRenderer,
} from '../../components/table/columns';
import { Query, useGenericPagination, usePagination } from '../../hooks/use-pagination';
import { FieldValues, useForm } from 'react-hook-form';
import { removeEmpty } from '../../utils/object-utils';
import { KeyValue } from '../../types/key-value';
import { MultiSelect, Option } from '../../components/forms/multi-select';

const ISOTime = `T00:00:00.000Z`;

export const Logs = (): JSX.Element => {
  const [auditPaginationQuery, setAuditPaginationQuery] = useState<Query>({});
  const logs = usePagination('/audit', auditPaginationQuery);

  const { register, handleSubmit, setValue } = useForm();
  const [searchQuery, setSearchQuery] = useState<KeyValue>({});
  const [searchPaginationQuery, setSearchPaginationQuery] = useState<Query>({});
  const searchedLogs = useGenericPagination('/audit/search', { ...searchQuery, ...searchPaginationQuery }, { enabled: searchQuery.query !== undefined });

  const onSubmit = (fields: FieldValues): void => {
    setSearchPaginationQuery({ page: 0 });
    setSearchQuery({ query: '', ...removeEmpty(fields) });
  };

  const onSelectChange = (name: string, options: Option[]): void => {
    setValue(name, options.map((option: Option) => option.value).toString());
  };

  const columns = [
    { accessor: 'userId', Header: 'User Id', sort: true, Cell: CellRenderer },
    { accessor: 'rootType', Header: 'Type', sort: true, Cell: CellRenderer },
    { accessor: 'entityId', Header: 'Entity Id', sort: true, Cell: CellJsonRenderer, width: 300 },
    { accessor: 'actionType', Header: 'Action Type', sort: true, Cell: CellRenderer },
    { accessor: 'actionTime', Header: 'Action Time', sort: true, Cell: CellDateRenderer },
  ];

  return <Content>
    <Page.Header>
      <div className="mb-2">
        <Page.Title>Change Log</Page.Title>
      </div>
      <Page.SubTitle>
        View the change log to see configuration changes made within the system.
      </Page.SubTitle>
    </Page.Header>
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row mb-4">
        <div className="col-4">
          <Label>User ID</Label>
          <TextInputLarge {...register('userId')} placeholder="User Id" />
        </div>
        <div className="col-4">
          <Label>Shared ID</Label>
          <TextInputLarge {...register('sharedId')} placeholder="Shared Id" />
        </div>
        <div className="col-4">
          <Label>Change Type</Label>
          <MultiSelect name="changeType" options={changeTypes} onChange={onSelectChange} />
        </div>
      </div>
      <div className="row mb-4">
        <div className="col-4">
          <Label>Root Type</Label>
          <MultiSelect name="rootType" options={rootTypes} onChange={onSelectChange} />
        </div>
        <div className="col-4">
          <Label>Date From</Label>
          <div className="d-flex align-items-center">
            <DatePicker name="dateFrom" onChange={(date: string): void => setValue('dateFrom', `${date}${ISOTime}`)} />
            <span className="d-flex ms-2">00:00</span>
          </div>
        </div>
        <div className="col-4">
          <Label>Date To</Label>
          <div className="d-flex align-items-center">
            <DatePicker name="dateTo" onChange={(date: string): void => setValue('dateTo', `${date}${ISOTime}`)} />
            <span className="d-flex ms-2">00:00</span>
          </div>
        </div>
      </div>
      <div className="row justify-content-end">
        <div className="col-4 justify-content-end d-flex">
          <Buttons.Primary type="submit" disabled={searchedLogs.isFetching}>Search</Buttons.Primary>
        </div>
      </div>
      <div className="separator" />
      <div className="row mb-4">
        <div className="col-4 log-search">
          <Page.Search {...register('query')} />
        </div>
      </div>
    </form>
    { Object.keys(searchQuery).length > 1 ? (
      <PaginatedTable isLoading={searchedLogs.isLoading} key="searchTable" columns={columns} data={searchedLogs.data || []} onChange={setSearchPaginationQuery} />
    ) : (
      <PaginatedTable isLoading={logs.isLoading}  key="auditTable" columns={columns} data={logs.data || []} onChange={setAuditPaginationQuery} />
    )}
  </Content>;
};

const rootTypes = [
  { label: 'Role', value: 'Role' },
  { label: 'Configuration', value: 'Configuration' },
  { label: 'LayerMetadata', value: 'LayerMetadata' },
  { label: 'LayerExtent', value: 'LayerExtent' },
  { label: 'Options', value: 'Options' },
  { label: 'Protocol', value: 'Protocol' },
  { label: 'WMTSTileLayer', value: 'WMTSTileLayer' },
  { label: 'LayerLayer', value: 'LayerLayer' },
  { label: 'Folder', value: 'Folder' },
  { label: 'WMSRasterLayer', value: 'WMSRasterLayer' },
  { label: 'LayerSwatch', value: 'LayerSwatch' },
  { label: 'AppSettings', value: 'AppSettings' },
  { label: 'LayerTranslation', value: 'LayerTranslation' },
  { label: 'FieldTranslation', value: 'FieldTranslation' },
  { label: 'Layer', value: 'Layer' },
  { label: 'AGSLayer', value: 'AGSLayer' },
  { label: 'AppUser', value: 'AppUser' },
  { label: 'VirtualLayer', value: 'VirtualLayer' },
  { label: 'MapService', value: 'MapService' },
  { label: 'Field', value: 'Field' },
  { label: 'UrlPrefix', value: 'UrlPrefix' },
  { label: 'MapServer', value: 'MapServer' },
  { label: 'Capability', value: 'Capability' },
  { label: 'Department', value: 'Department' },
  { label: 'LayerConfiguration', value: 'LayerConfiguration' }
];

const changeTypes = [
  { label: 'INSERT', value: 'INSERT' },
  { label: 'UPDATE', value: 'UPDATE' },
  { label: 'DELETE', value: 'DELETE' },
  { label: 'REVERT', value: 'REVERT' }
];
